// Notes editor schema.
//
// A small ProseMirror schema for the writer's scratchpad. Separate
// from the screenplay editor's schema (which has scene_heading,
// character, dialogue, etc.) — notes are normal prose with the
// classic markdown blocks. Keeping the two schemas isolated means
// the screenplay's release-blocking pagination paths can't be
// affected by anything we ship here.
//
// Block group: paragraph, heading (h1–h3), bullet_list, ordered_list,
// blockquote, code_block, horizontal_rule. Inline marks: strong, em,
// code, link. We deliberately do NOT support tables, images, or HTML
// passthrough — same set as markdown.js (the read-only renderer the
// chat collaborator uses) so what the writer types is what they'll
// see persisted.

import { Schema } from 'prosemirror-model';

export const notesSchema = new Schema({
  nodes: {
    doc: { content: 'block+' },

    paragraph: {
      group: 'block',
      content: 'inline*',
      parseDOM: [{ tag: 'p' }],
      toDOM: () => ['p', 0],
    },

    heading: {
      group: 'block',
      content: 'inline*',
      attrs: { level: { default: 1 } },
      defining: true,
      parseDOM: [
        { tag: 'h1', attrs: { level: 1 } },
        { tag: 'h2', attrs: { level: 2 } },
        { tag: 'h3', attrs: { level: 3 } },
      ],
      toDOM: (node) => [`h${node.attrs.level}`, 0],
    },

    blockquote: {
      group: 'block',
      content: 'block+',
      parseDOM: [{ tag: 'blockquote' }],
      toDOM: () => ['blockquote', 0],
    },

    code_block: {
      group: 'block',
      content: 'text*',
      marks: '',
      code: true,
      defining: true,
      parseDOM: [{ tag: 'pre', preserveWhitespace: 'full' }],
      toDOM: () => ['pre', ['code', 0]],
    },

    horizontal_rule: {
      group: 'block',
      parseDOM: [{ tag: 'hr' }],
      toDOM: () => ['hr'],
    },

    bullet_list: {
      group: 'block',
      content: 'list_item+',
      parseDOM: [{ tag: 'ul' }],
      toDOM: () => ['ul', 0],
    },

    ordered_list: {
      group: 'block',
      content: 'list_item+',
      attrs: { order: { default: 1 } },
      parseDOM: [
        {
          tag: 'ol',
          getAttrs(dom) {
            const start = dom.getAttribute('start');
            return { order: start ? Number(start) : 1 };
          },
        },
      ],
      toDOM: (node) =>
        node.attrs.order === 1 ? ['ol', 0] : ['ol', { start: node.attrs.order }, 0],
    },

    list_item: {
      // Each list item holds at least one paragraph; nested lists are
      // allowed but not exposed via input rules (a writer can still
      // paste structured markdown that contains them).
      content: 'paragraph block*',
      defining: true,
      parseDOM: [{ tag: 'li' }],
      toDOM: () => ['li', 0],
    },

    text: { group: 'inline' },
  },

  marks: {
    strong: {
      parseDOM: [{ tag: 'strong' }, { tag: 'b' }],
      toDOM: () => ['strong', 0],
    },
    em: {
      parseDOM: [{ tag: 'em' }, { tag: 'i' }],
      toDOM: () => ['em', 0],
    },
    code: {
      parseDOM: [{ tag: 'code' }],
      toDOM: () => ['code', 0],
    },
    link: {
      attrs: { href: {} },
      inclusive: false,
      parseDOM: [
        {
          tag: 'a[href]',
          getAttrs(dom) {
            return { href: dom.getAttribute('href') };
          },
        },
      ],
      toDOM: (node) => ['a', { href: node.attrs.href, target: '_blank', rel: 'noreferrer' }, 0],
    },
  },
});
